/**
 * <programme-calendar> — an event's ticket types read as its own programme
 * calendar: one horizontal band, every programme day present at once, each
 * type filed under the day its entry window opens on, and the types that hold
 * for the whole run standing as the band's leading column.
 *
 * Mounted by both admin surfaces that list ticket types — the console's
 * "Ticketarten" block (console/dashboard.view.ts) and the event form's
 * ticket-type section (event-form.view.ts) — because the two were the same
 * question asked twice and answered differently. The console glances and the
 * editor edits, and both open a type the same way: the row is the door. What
 * the editing surface adds on top of that one interaction — a drag handle, a
 * duplicate action, a chain warning — rides the row rather than the layout,
 * and `editable` is what turns those additions on.
 *
 * WHY A BAND AND NOT THE SHOP'S DAY CHIPS. <programme-day-selector> picks ONE
 * day and filters, because a buyer stands on a day. An operator is never on
 * one day — they are accounting for the whole programme — so this surface
 * spends horizontal space to show every day and asks for no selection at all.
 *
 * A DAY IS A CONTAINER, NOT A CAPTION. One day is one glass pane: its head at
 * the top and that day's types as separately-surfaced blocks inside it. A head
 * card above separately-carded rows put the day and its types at one eye level,
 * two peers, when the day is what the types are inside of. Nothing is drawn on
 * the seam between head and body — the head's own tint is already a step from
 * the blocks below it, so a rule there is a third mark saying what two already
 * say, and a full-bleed horizontal line in a column of rows reads as one more
 * divider in a surface that has no dividers. What the seam carries instead is
 * air: the head's `pb-3` above the list's own top padding.
 *
 * EVERY HEAD OPENS THE SAME WAY. A head is a filled circular token in the
 * leading slot, a word beside it, and a count on the right rail — the day's
 * date numeral and the whole-event block's calendar glyph are two instances of
 * one shape, because a head that skips the token reads as a row. The token
 * carries the column's IDENTITY and the words beside it are chrome, set
 * uppercase and wide-tracked at label scale: a register no row speaks, which is
 * what lets a head be quieter than its own rows and still read as their lid.
 *
 * ONE RUNG DOWN PER LEVEL IN. The pane owns the outer curve at `xl`, a type
 * block inside it sits at `lg`, and a head — full-bleed against the pane's own
 * edge — spells `rounded-none`, because a head that curved would be a second
 * outer corner inside the first. <schmancy-surface> brings a `2xl` corner of
 * its own, which inside the pane's `xl` is a child rounder than its parent,
 * geometrically impossible and visible as a mis-struck corner where the pane's
 * `overflow-hidden` clips it; that default is why every head here spells the
 * zero rather than inheriting one.
 *
 * A TYPE IS ITS OWN SURFACE, AND THE STEP IS ONE RUNG. A row is one to four
 * lines deep, so rhythm alone cannot say where one type stops and the next
 * starts: each row is its own block, inset by `mx-2`, held apart by `mb-2`,
 * and tinted `containerLow` against the `containerLowest` glass pane it sits
 * in. ONE rung — a row tinted `containerHigh` over the same pane made every
 * type shout at the volume the report should have had, six columns of slabs
 * with no hierarchy bought by any of it, while a hairline rule instead of a
 * block measured 1.38:1 in the dark theme and separated nothing a reader could
 * see. The step is what states the boundary; the spacing is what keeps the
 * types from touching; the pane stays quiet under both.
 *
 * THE LEADING SLOT IS ONE TOKEN, AND CAPACITY IS ITS CENTRE. The ring's centre
 * states the capacity, its arc the proportion sold above it across a hairline,
 * so a type's quota and its sales are each stated once and the row's own column
 * is left to the name and the price under it. A full-width linear meter instead
 * was the row's only strong horizontal mark, so it read as the boundary between
 * types while also being the measurement — one mark doing two jobs, and neither
 * legible. A ring claims no horizontal span, which leaves the separating
 * entirely to the surface above.
 *
 * The ARC is what the figure draws: `sold` null is a surface that cannot read
 * sales, so the track stands alone with the capacity in it. The EDITING mount
 * draws the token either way, because capacity is a fact it owns and has
 * nowhere else to put — the row prints no meta line restating what the name,
 * the price and this token already carry. The reporting mount draws it only
 * with a figure, having no capacity of its own to state. Both open on the same
 * mark, because every affordance `editable` turns on rides the trailing rail.
 *
 * A BAND THAT HIDES ITS SCROLLBAR OWES ITS POSITION, AND PAYS IT AT THE EDGE.
 * Nothing else in this layout says it: columns are a fixed `w-72`, so what
 * shows past the cut is whatever the window's own width leaves over a column,
 * which at this surface's measure is a sliver of blank card — a band ending
 * rather than a band continuing. So <schmancy-scroll-rail> wraps
 * <schmancy-scroll hide> and states the position where the question is asked:
 * a shade over whichever cut still has content behind it and a chevron riding
 * that fold, each absent the moment there is nothing more that way and both
 * absent while the whole band fits. The mark is pressable and steps one
 * column, and it is out of the accessibility tree — every column is already
 * reachable in DOM order, so a reader and a keyboard gain nothing from a
 * shortcut over it.
 *
 * A TALL DAY GROWS THE BAND, NEVER SCROLLS INSIDE IT. Each column flows to its
 * natural height and the page's own scroller catches the excess; two scroll
 * axes on one box is what makes a column's last row unreachable.
 *
 * PAST DAYS ARE SHOWN — the deliberate inversion of the shop's rule. A buyer
 * cannot buy a past day; a past day's sold figures are exactly what an
 * operator is accounting for. Only the heading dims, because the numbers are
 * the report.
 *
 * COPYING A DAY IS AN ACTION OF THE DAY'S OWN HEAD. The editor mount puts a
 * quiet `content_copy` at the end of each head's rail, and pressing it says
 * which day is being copied — the band already answers that question, so a
 * sheet that opens by asking it again is a step the operator has already
 * taken. It dispatches `cloneDay` with the day's key — this surface's host
 * event scoped to one day, where `open`/`duplicate` are scoped to one row and
 * `reorder` to the whole list. A day with no rows filed on it has nothing to
 * copy and a single-day programme has nowhere to copy onto, so neither draws
 * it. A day whose rows are all multi-day passes counts as nothing to copy: a
 * pass admits on every day it covers and so belongs to no single day the
 * sheet could copy FROM, which is why the gate reads the day's SINGLE-day
 * rows rather than its row count.
 * a PAST day draws it in full ink, because last Saturday onto next
 * Saturday is the likeliest copy there is. It is never hover-revealed — a
 * tablet has no hover, and this trigger is now the feature's only door.
 *
 * A COLUMN IS THE DAY A DROP MEANS. Each day column carries its own day key
 * as the drag primitive's zone and the whole-event column carries
 * `whole-event`, so a row dropped in a column other than the one it was
 * dragged out of dispatches `moveToDay` with that column's day — null from the
 * whole-event column, which is the inherit state, the same host-event
 * vocabulary `cloneDay` speaks for a day head. The `reorder` event still goes
 * out unchanged beside it: the order is the operator's own sequence and the
 * day is the document's, two facts and therefore two writes.
 *
 * A MULTI-DAY TYPE FILES UNDER ITS START DAY and carries a span token instead
 * of a second heading: three headings over one row is three doors to one row,
 * plus a triple count in every roll-up it touched. The shop groups by
 * membership because "what can I buy for Saturday" is a membership question;
 * an operator's question is identity.
 *
 * THE GRID IS THE BAND'S ALONE. Ticket types are a list wherever they appear
 * — inside a column, in the one-day degrade, in the whole-event column —
 * because a type is a line of an inventory, and a card grid of types reads as
 * a set of things to choose between.
 *
 * THE WHOLE EVENT IS THE COLUMN THE BAND OPENS ON. A type that holds for the
 * whole run belongs to every day and to no day, so it takes the leading
 * column: the operator reads what is always on sale before reading what is on
 * sale today, and the pass travels with the days rather than sitting under
 * them as a second surface. Repeated into every column a festival pass would
 * appear four times and invite "do I need one per day". It wears a day
 * column's own card, head and list, because one band carries one grouping
 * vocabulary and never two, and its name is set in that vocabulary's own head
 * register rather than its rows'. At `title md` the name tied with every row
 * name beneath it — same face, same size, same ink — so the head read as the
 * list's FIRST LINE rather than its lid; a day head never has that problem,
 * because its identity is the numeral inside the chip and the words beside it
 * are chrome. This column has no numeral to head with, so the glyph is the
 * identity and the name joins the chrome: uppercase, wide-tracked, `label md`
 * for the one line it sets where a day head's date breaks into two. Its count
 * stays `label sm`, because it counts ticket types rather than seats and must
 * not share the sold/capacity rail.
 *
 * AIR INSIDE A CARD IS THE CARD'S TO SET. A list of type blocks owes its
 * container more air at the head seam than the blocks owe each other, or the
 * first block reads as one more item in a stack rather than as the first thing
 * inside a pane; `LIST_AIR` is that padding, set on the host, where a consumer
 * utility outranks <schmancy-list>'s own `:host` default.
 *
 * The degrades: one programme day with nothing running the whole event is one
 * card at its own measure, never a scroller with one column in it and never a
 * full-width stretch that strands the date at one end and the numbers at the
 * other — one day beside a whole-event column is already two columns, and two
 * columns are a band; no programme days at all
 * — most events — is one headless card of the same lines, so a host with no
 * day dimension mounts this surface rather than keeping a second list of its
 * own.
 */
import {
	reorder,
	type SchmancyReorderEvent,
} from "@mhmo91/schmancy/directives";
// Side-effect: registers <schmancy-scroll> and <schmancy-scroll-rail>.
import "@mhmo91/schmancy/layout";
import { SchmancyElement, schmancyElement } from "@mhmo91/schmancy/mixins";
// Side-effect: registers <schmancy-visually-hidden>.
import "@mhmo91/schmancy/visually-hidden";
import { formatCurrency, formatNumber } from "@mhmo91/rx";
import { type Event, TicketType } from "@momo/types";
import {
	css,
	html,
	nothing,
	type PropertyValues,
	type TemplateResult,
} from "lit";
import { property } from "lit/decorators.js";
import { ifDefined } from "lit/directives/if-defined.js";
import { repeat } from "lit/directives/repeat.js";
import { when } from "lit/directives/when.js";
import { LocalizeMixin } from "../i18n/localize.ts";
import "./console/sell-through-ring.view.ts";

// Orders carry no currency field and every money call site in this package
// (dashboard.view.ts, event-page.view.ts, type-selector.view.ts) pins EUR the
// same way.
const CURRENCY = "EUR";

/** Every ungrouped pane in this surface — the single-day degrade and the
 *  no-day-grouping arm. A row is one leading token and one column of text
 *  beside it, and a name set across a full 1600px is a line the eye travels
 *  rather than reads; the cap is what keeps both ungrouped arms the same
 *  instrument the band's own `w-72` columns are. The empty space it leaves at
 *  a wide measure is the honest cost of the one-axis rule — a second column is
 *  what it must never become. */
export const FLAT_PANE = "block w-full max-w-md overflow-hidden rounded-xl";

/** One column of any band in this product — the whole-event column and every
 *  day column, here and in the door's own accepted-types band, one measure,
 *  because a band reads as one vocabulary or as none and two bands a column
 *  width apart read as two. */
export const COLUMN = "block w-72 shrink-0 overflow-hidden rounded-xl";

/** The air a list of type blocks owes its container at top and bottom. */
export const LIST_AIR = "py-3";

/** One ticket type as the calendar renders it — the day is absent by design:
 *  the heading above the row is the day, which is the whole argument of this
 *  surface. */
export type ProgrammeCalendarRow = {
	readonly id: string;
	readonly name: string;
	readonly price: number;
	readonly capacity: number;
	/** Seats already sold, or null wherever the number cannot be read at all —
	 *  a session without sales-report access, and an editor opened away from
	 *  the console that holds this event's orders. Null is not zero: a row
	 *  stating 0 sold where it simply cannot see the figure states a
	 *  fabrication, so the arc is drawn by the figure's presence and never by
	 *  which mount is asking. */
	readonly sold: number | null;
	/** Paid revenue in minor units, null on the same terms as `sold`. */
	readonly revenue: number | null;
	/** The type's own EFFECTIVE entry window (TicketType.resolveEntryWindow) in
	 *  epoch millis, and the zone it is read in — the day and the span are
	 *  derived from these two and from nothing else, because a stored day
	 *  beside the window is a second representation free to contradict it. */
	readonly entryWindow: { readonly opensAt: number; readonly closesAt: number };
	readonly timezone: string;
	/** One already-localized line beneath the name — the predecessor a chained
	 *  type shows after. Price and capacity are not among the things it may
	 *  carry: the row prints the price under the name and the leading token's
	 *  centre carries the capacity, and a line restating either is that fact's
	 *  second home. Absent on a surface that has none, so a row model carrying
	 *  no editorial copy needs no mapping layer to say so. */
	readonly note?: string | null;
	/** One already-localized warning sentence, rendered in error ink, on the
	 *  same terms as `note`. */
	readonly warning?: string | null;
};

/** One day column: the day's own dimension — its widest door hours across
 *  every window that ADMITS on it — plus the rows FILED on it, which is the
 *  narrower question of which windows START there. Filing is identity, hours
 *  are membership: a multi-day pass counts once, under its first day, while
 *  still contributing its hours to every day it admits on. */
type DayGroup = TicketType.ProgrammeDay & {
	readonly rows: readonly ProgrammeCalendarRow[];
};

@schmancyElement("programme-calendar")
export class ProgrammeCalendarView extends LocalizeMixin(SchmancyElement) {
	static styles = [
		css`
			:host {
				display: block;
			}
		`,
	];

	/** Every ticket type this event has, in the operator's own order — the
	 *  calendar splits day-bound from whole-event itself, so no caller has to
	 *  answer that question a second time and differently. */
	@property({ attribute: false }) rows: readonly ProgrammeCalendarRow[] = [];

	/** The event the windows are read against — a window spanning the whole
	 *  schedule is tied to no day (TicketType.coversWholeEvent). */
	@property({ attribute: false }) event: Pick<Event.Event, "schedule"> | null =
		null;

	/** Whether this surface edits rather than reports: turns on the drag
	 *  handle, the row's duplicate action, and the day head's clone trigger. */
	@property({ type: Boolean }) editable = false;

	/** The venue zone the mounter resolved — the one zone the clone trigger's
	 *  own gate reads, and the same `tz` the day-clone sheet builds its target
	 *  days in, so the two can never disagree about how many days the schedule
	 *  spans. A row's own effective zone is not that zone whenever the type
	 *  overrides `locationId`. Unset leaves the trigger undrawn; the reporting
	 *  mount sets neither this nor `editable`. */
	@property({ type: String }) timezone = "";

	/** The instant "today" is read against, for a mount that has to render a
	 *  fixed catalog at a chosen point in its own run (the studio's clock
	 *  chips). Unset — every product mount — is the wall clock. */
	@property({ attribute: false }) now: number | null = null;

	/** The band scrolls today into view once per day set, never on every
	 *  render — re-scrolling would drag the band back under the operator's own
	 *  hand every time anything above it changed. */
	#scrolledFor = "";

	/** The primitive's own move, re-dispatched as this host's public contract.
	 *  `reorder()` fires on the list container and is deliberately not
	 *  composed, so it stops at this shadow root; the host's own event is what
	 *  the two mounting surfaces (console, editor) already listen for. */
	readonly #persistOrder = (e: SchmancyReorderEvent) => {
		this.dispatchEvent(
			new CustomEvent("reorder", {
				detail: { orderedIds: e.detail.orderedIds },
				bubbles: true,
				composed: true,
			}),
		);
		// Two writes, never one: the date is the operator's stated intent and
		// the order is cosmetic, so they travel as two events and the host
		// commits the date first.
		if (e.detail.zone === null) return;
		this.dispatchEvent(
			new CustomEvent("moveToDay", {
				detail: {
					id: e.detail.id,
					day: e.detail.zone === "whole-event" ? null : e.detail.zone,
				},
				bubbles: true,
				composed: true,
			}),
		);
	};

	protected override updated(changed: PropertyValues): void {
		super.updated(changed);
		const today = this.renderRoot.querySelector("[data-today]");
		const key = today?.getAttribute("data-today") ?? "";
		if (key === "" || this.#scrolledFor === key) return;
		this.#scrolledFor = key;
		today?.scrollIntoView({ inline: "start", block: "nearest" });
	}

	/** One ticket type's line. A LIST ROW AND NEVER A CARD, at every measure:
	 *  the grid is the day band's alone, and a type is one line of an inventory
	 *  rather than one of a set of things to choose between.
	 *
	 *  THE TEXT IS ONE COLUMN, THE SAME COLUMN IN BOTH MOUNTS. The name is the
	 *  first line and the price the second, so the row holds no right rail open
	 *  for a figure a mount may not have — and the name reads against the row's
	 *  whole measure rather than the half a facing price leaves it, which inside
	 *  a `w-72` column is the difference between reading a name and reading its
	 *  first word.
	 *
	 *  A NAME WRAPS BEFORE IT CLIPS, TO TWO LINES. The editing mount's trailing
	 *  rail costs 44px of a 272px row — 180px of text on the reporting mount
	 *  against 136px here — and nothing gives it back: the ring is the same 56px
	 *  on both mounts by design, and the rail is 32px wide whether it holds one
	 *  control or two stacked, so the handle standing in it costs no width at
	 *  all. A name too long for what is left therefore grows the row by a line
	 *  rather than hiding its ending, on the one mount that exists for
	 *  identifying a row before dragging or duplicating it. Two lines and no
	 *  more, because `name` carries no maximum length — and `maxLines` rather
	 *  than `truncate`, whose `white-space: nowrap` is what forbids the second
	 *  line in the first place.
	 *
	 *  EVERYTHING SUBORDINATE SHARES ONE LINE. Sold-out, the multi-day span and
	 *  the note are three qualifiers of one type, so they run inline across the
	 *  row's whole measure on the one line under the price, wrapping only when
	 *  they must, instead of stacking one short fragment per line down a column
	 *  half the row wide. A
	 *  warning is the one thing that still owns a line of its own, because it is
	 *  a sentence and it is read rather than glanced at.
	 *
	 *  THE NAME AND ITS PRICE ARE ONE BLOCK, AND BOTH ARE TITLES. The name is
	 *  `title md` and the price `title sm` directly under it, both in full ink:
	 *  the two facts an operator scans a nine-type column for are what a type
	 *  IS, so they read as one identity rather than a heading with a footnote
	 *  set at label scale beside it. Neither is the loudest mark in the row —
	 *  the ring is, by being the only one with an arc — and the second line is
	 *  cheapest exactly where a nine-type column is scanned: the ring's own 56px
	 *  was already most of a reporting row's height, so the stack adds six
	 *  pixels there. */
	/** The leading mark is `<sell-through-ring>` — the ring is what per-type
	 *  sold-of-quota wears, on the row that owns that type; the whole-event
	 *  roll-up above wears a bar instead, so the two readings never share a
	 *  shape. What the fraction, the null figure, the quota-less type and the
	 *  over-sold row each read as lives in that component's own header. */
	private row(row: ProgrammeCalendarRow): TemplateResult {
		// `capacity > 0` is load-bearing, exactly as the day head's own roll-up
		// reads it: a quota-less type is 0 - 0 <= 0, which means "no quota set"
		// and never "sold out".
		const soldOut =
			row.sold !== null && row.capacity > 0 && row.capacity - row.sold <= 0;
		const days = TicketType.daysCovered(row.entryWindow, row.timezone);
		const short = (day: string) =>
			new Date(`${day}T00:00:00.000Z`).toLocaleDateString(this.locale, {
				day: "numeric",
				month: "numeric",
				timeZone: "UTC",
			});
		return html`
			<schmancy-list-item
				class="mx-2 mb-2 block rounded-lg bg-surface-containerLow last:mb-0"
				data-reorder-item=${row.id}
				aria-label=${row.name}
				@click=${() => {
					// The row is the door on every mount: one gesture opens a type
					// wherever this surface is shown. The trailing rail carries only
					// what is NOT opening, and each control on it stops the event so
					// pressing one never opens the row it stands on.
					this.dispatchEvent(
						new CustomEvent("open", {
							detail: { id: row.id },
							bubbles: true,
							composed: true,
						}),
					);
				}}
			>
				${when(
					// The editing mount owns the capacity and has nowhere else to
					// state it — no meta line, no rail — so the token stands whether
					// or not a figure exists. The reporting mount has no capacity of
					// its own to state and draws the token only with one.
					this.editable || row.sold !== null,
					() =>
						html`<sell-through-ring
							slot="leading"
							.sold=${row.sold}
							.capacity=${row.capacity}
							size="row"
						></sell-through-ring>`,
				)}
				<div class="grid w-full min-w-0 grid-cols-1 gap-y-0.5 py-2">
					<schmancy-typography
						type="title"
						token="md"
						.maxLines=${2}
						class="min-w-0 break-words"
					>
						${row.name}
					</schmancy-typography>
					<schmancy-typography type="title" token="sm" class="tabular-nums">
						${formatCurrency(row.price / 100, CURRENCY)}
					</schmancy-typography>
					${when(
						soldOut || days.length > 1 || (row.note ?? null) !== null,
						() => html`
							<span class="flex min-w-0 flex-wrap items-center gap-x-2">
								${when(
									soldOut,
									() => html`
										<schmancy-typography type="label" token="xs" class="text-success-default">
											${this.t("admin.events.console.soldOutLabel")}
										</schmancy-typography>
									`,
								)}
								${when(
									days.length > 1,
									() => html`
										<schmancy-typography type="label" token="xs" class="truncate text-surface-onVariant">
											<schmancy-icon class="align-middle text-sm">date_range</schmancy-icon>
											${short(days[0] ?? "")}–${short(days.at(-1) ?? "")}
										</schmancy-typography>
									`,
								)}
								${when(
									(row.note ?? null) !== null,
									() => html`
										<schmancy-typography type="label" token="xs" class="min-w-0 text-surface-onVariant">
											${row.note}
										</schmancy-typography>
									`,
								)}
							</span>
						`,
					)}
					${when(
						(row.warning ?? null) !== null,
						() => html`
							<schmancy-typography
								type="label"
								token="xs"
								class="flex items-start gap-1 text-error-default"
							>
								<schmancy-icon class="text-sm">warning</schmancy-icon>
								${row.warning}
							</schmancy-typography>
						`,
					)}
				</div>
				${when(
					this.editable,
					() => html`
						<div slot="trailing" class="flex flex-col">
							<!-- The handle is the span, not the glyph inside it: a
							     <schmancy-icon> hides ITSELF from assistive tech, so a
							     handle drawn as one is named and focusable and announced
							     to nobody. The span also gives the gesture the same 32px
							     square its neighbour stands on, so the rail reads as one
							     column of controls rather than two loose marks. -->
							<span
								data-reorder-handle
								class="grid size-8 shrink-0 place-items-center text-surface-onVariant"
								aria-label=${this.t(
									"admin.events.form.reorderTicketTypeAriaLabel",
									{ name: row.name },
								)}
								@click=${(e: Event) => e.stopPropagation()}
							>
								<schmancy-icon class="text-xl">drag_indicator</schmancy-icon>
							</span>
							<schmancy-icon-button
								variant="text"
								size="xs"
								icon="content_copy"
								aria-label=${this.t(
									"admin.events.form.duplicateTicketTypeAriaLabel",
									{ name: row.name },
								)}
								@click=${(e: Event) => {
									e.stopPropagation();
									this.dispatchEvent(
										new CustomEvent("duplicate", {
											detail: { id: row.id },
											bubbles: true,
											composed: true,
										}),
									);
								}}
							></schmancy-icon-button>
						</div>
					`,
				)}
			</schmancy-list-item>
		`;
	}

	/** The day's head — the first child of the day's own pane, above that day's
	 *  lines and separated from them by air alone. A day is a CONTAINER, not a
	 *  caption: a head card sitting above separately-carded rows made the day and
	 *  its types two peers at one eye level, when the day is what the types are
	 *  inside of.
	 *
	 *  ONE TONAL STEP BETWEEN A HEAD AND ITS LINES, AND TODAY OWNS THE ONLY
	 *  SECOND ONE. A head rides its rows' own `containerLow` rung, full-bleed
	 *  where they are inset, and today's alone rises to `container`. A three-rung
	 *  tense ladder climbing to `containerHighest` spent contrast on a fact the
	 *  date numeral already carries and left six columns of loud blocks; pastness
	 *  is read off the dimmed temporal half alone — numeral, weekday, month —
	 *  while the numbers stay full ink, because the day is over and the report is
	 *  not.
	 *
	 *  Today costs no extra line: a `bg-primary-default` date numeral, the word
	 *  itself carried to assistive tech only, because a visible "today" pushed
	 *  today's hours a line below its neighbours' and ragged the band's top edge.
	 *  Every other day's numeral is a filled chip one rung above its own head — a
	 *  bare numeral beside a filled one reads as two kinds of thing, and a chip on
	 *  every head is the glance-level mark that says "head". */
	private dayHeading(
		group: DayGroup,
		today: string,
		cloneable: boolean,
	): TemplateResult {
		const isToday = group.day === today;
		const past = group.day < today;
		const calendar = new Date(`${group.day}T00:00:00.000Z`);
		const datePart = (opts: Intl.DateTimeFormatOptions) =>
			calendar.toLocaleDateString(this.locale, { ...opts, timeZone: "UTC" });
		const clock = (millis: number) =>
			new Date(millis).toLocaleTimeString(this.locale, {
				hour: "2-digit",
				minute: "2-digit",
				timeZone: group.timezone,
			});
		// The roll-up sums the rows FILED on this day, so a multi-day pass is
		// counted once — under its start day — rather than once per day it
		// admits on, which would triple the programme's own capacity.
		const capacity = group.rows.reduce((sum, r) => sum + r.capacity, 0);
		const reported = group.rows.filter((r) => r.sold !== null);
		const sold = reported.reduce((sum, r) => sum + (r.sold ?? 0), 0);
		const revenue = reported.reduce((sum, r) => sum + (r.revenue ?? 0), 0);
		// `capacity > 0` is load-bearing: a day of quota-less types sums to
		// zero, and 0 >= 0 would read as sold out when it means "no quota".
		const soldOut = capacity > 0 && sold >= capacity;
		return html`
			<div
				class=${this.classMap({
					"rounded-none": true,
					"bg-surface-container": isToday,
					"bg-surface-containerLow": !isToday,
				})}
			>
				<div class="flex items-center gap-2.5 px-3 pt-2.5 pb-3">
					<span
						class=${this.classMap({
							"grid size-9 shrink-0 place-items-center rounded-full": true,
							"bg-primary-default": isToday,
							"bg-surface-container": !isToday,
						})}
						aria-hidden="true"
					>
						<schmancy-typography
							type="title"
							token="lg"
							class=${this.classMap({
								"tabular-nums": true,
								"text-primary-on": isToday,
								"text-surface-onVariant": past,
							})}
						>
							${datePart({ day: "2-digit" })}
						</schmancy-typography>
					</span>
					<span
						class=${this.classMap({
							"flex flex-col": true,
							"text-surface-onVariant": past,
						})}
					>
						<!-- Weekday and month are two fragments of one date; read out one
						     by one they are not a date, so the whole date is carried here
						     instead and the fragments are decoration. -->
						<schmancy-visually-hidden>
							${datePart({ weekday: "long", day: "numeric", month: "long" })}${isToday ? `, ${this.t("admin.events.console.todayLabel")}` : ""}
						</schmancy-visually-hidden>
						<schmancy-typography
							type="label"
							token="xs"
							transform="uppercase"
							class="tracking-widest"
							aria-hidden="true"
						>
							${datePart({ weekday: "short" })}
						</schmancy-typography>
						<schmancy-typography
							type="label"
							token="xs"
							transform="uppercase"
							class="tracking-widest"
							aria-hidden="true"
						>
							${datePart({ month: "short" })}
						</schmancy-typography>
					</span>
					<span class="ml-auto flex min-w-0 flex-col items-end">
						<schmancy-typography type="label" token="sm" class="tabular-nums text-surface-onVariant">
							${clock(group.opensAt)} – ${clock(group.closesAt)}
						</schmancy-typography>
						<span class="flex items-baseline gap-1.5">
							<schmancy-typography
								type="title"
								token="md"
								class=${this.classMap({
									"tabular-nums": true,
									"text-success-default": soldOut,
								})}
							>
								${
									reported.length === 0
										? formatNumber(capacity, this.locale)
										: `${formatNumber(sold, this.locale)} / ${formatNumber(capacity, this.locale)}`
								}
							</schmancy-typography>
							${when(
								reported.length > 0,
								() => html`
									<schmancy-typography type="label" token="xs" class="tabular-nums text-surface-onVariant">
										${formatNumber(revenue / 100, this.locale, {
											style: "currency",
											currency: CURRENCY,
											maximumFractionDigits: 0,
										})}
									</schmancy-typography>
								`,
							)}
						</span>
					</span>
					${when(
						this.editable &&
							cloneable &&
							group.rows.some(
								(r) =>
									TicketType.daysCovered(r.entryWindow, r.timezone).length ===
									1,
							),
						() => html`
							<schmancy-icon-button
								variant="text"
								size="xs"
								icon="content_copy"
								class="-mr-1"
								aria-label=${this.t(
									"admin.ticketTypes.dayClone.dayTriggerAriaLabel",
									{
										day: datePart({
											weekday: "long",
											day: "numeric",
											month: "long",
										}),
									},
								)}
								@click=${() => {
									this.dispatchEvent(
										new CustomEvent("cloneDay", {
											detail: { day: group.day },
											bubbles: true,
											composed: true,
										}),
									);
								}}
							></schmancy-icon-button>
						`,
					)}
				</div>
			</div>
		`;
	}

	override render(): TemplateResult | typeof nothing {
		const event = this.event;
		if (event === null) return nothing;
		const dayed = this.rows.filter(
			(r) => !TicketType.coversWholeEvent(r.entryWindow, event),
		);
		const wholeEvent = this.rows.filter((r) =>
			TicketType.coversWholeEvent(r.entryWindow, event),
		);
		// The day an operator FINDS a row under is its window's first day; the
		// groups keep the operator's own row order within each day.
		const byDay = new Map<string, ProgrammeCalendarRow[]>();
		for (const row of dayed) {
			const day = TicketType.daysCovered(row.entryWindow, row.timezone)[0];
			if (day === undefined) continue;
			const bucket = byDay.get(day);
			if (bucket) bucket.push(row);
			else byDay.set(day, [row]);
		}
		// The whole surface is ONE reorder list, whichever grouping is drawn
		// above it: a row dragged from one day column onto another lands where
		// it was dropped in the operator's own order, exactly as the flat list
		// moves. The primitive owns the gesture; this surface owns only the
		// language its keyboard announcements are read out in.
		const ids = this.rows.map((r) => r.id);
		const reorderOptions = {
			disabled: !this.editable,
			labels: {
				grabbed: (name: string, position: number, total: number) =>
					this.t("admin.events.form.reorderGrabbed", {
						name,
						position,
						total,
					}),
				moved: (name: string, position: number, total: number) =>
					this.t("admin.events.form.reorderMoved", { name, position, total }),
				dropped: (name: string, position: number, total: number) =>
					this.t("admin.events.form.reorderDropped", {
						name,
						position,
						total,
					}),
				cancelled: (name: string) =>
					this.t("admin.events.form.reorderCancelled", { name }),
			},
		};
		// NO DAY GROUPING AT ALL — most events. One headless card of the same
		// list lines a day column holds, because the row is one instrument and
		// the grouping above it is what varies: flat, no-windows, console and
		// editor are then one arm rather than four shapes of the same list.
		if (byDay.size === 0)
			return this.rows.length === 0
				? nothing
				: html`
					<schmancy-surface
						type="containerLowest"
						opacity="glass"
						elevated
						class=${FLAT_PANE}
						${reorder(ids, reorderOptions)}
						@reorder=${this.#persistOrder}
					>
						<schmancy-list surface="transparent" class=${LIST_AIR}>
							${repeat(
								this.rows,
								(r) => r.id,
								(r) => this.row(r),
							)}
						</schmancy-list>
					</schmancy-surface>
				`;
		// One derivation for both questions the head asks: programmeDaysOf
		// answers the hours by MEMBERSHIP (so a multi-day pass contributes its
		// own hours to every day it admits on), and the filing map answers
		// which rows the day owns. A day nothing starts on gets no column.
		const groups: readonly DayGroup[] = TicketType.programmeDaysOf(
			dayed.map((r) => ({ ...r.entryWindow, timezone: r.timezone })),
		).flatMap((day) => {
			const rows = byDay.get(day.day);
			return rows ? [{ ...day, rows }] : [];
		});

		const today = TicketType.dayKeyOf(
			this.now ?? Date.now(),
			dayed[0]?.timezone ?? "UTC",
		);
		// Where a copy could LAND is the event's own schedule span, not the days
		// its types happen to occupy: a second day with no types on it yet is
		// exactly the day an operator copies onto. The span is read in the venue
		// zone the mounter passes, which is the zone the sheet builds its target
		// days in — a row's own zone can be another venue's, and one schedule
		// span is one day in one zone and two in another.
		const cloneable =
			this.timezone !== "" &&
			TicketType.daysCovered(
				{
					opensAt: event.schedule.start.toMillis(),
					closesAt: event.schedule.end.toMillis(),
				},
				this.timezone,
			).length > 1;

		return html`
			<div
				class="flex flex-col gap-4"
				${reorder(ids, reorderOptions)}
				@reorder=${this.#persistOrder}
			>
				${
					groups.length === 1 && groups[0] && wholeEvent.length === 0
						? html`
							<!-- One column is one card, not a band: no scroller, because a
							     single column beside an empty band is an affordance leading
							     nowhere. It stays at its own measure rather than stretching,
							     since a single list across a wide page strands the date at
							     one end and the numbers at the other. -->
							<schmancy-surface type="containerLowest" opacity="glass" elevated class=${FLAT_PANE}>
								${this.dayHeading(groups[0], today, cloneable)}
								<schmancy-list surface="transparent" class=${LIST_AIR}>
									${repeat(
										groups[0].rows,
										(r) => r.id,
										(r) => this.row(r),
									)}
								</schmancy-list>
							</schmancy-surface>
						`
						: html`
							<schmancy-scroll-rail>
								<schmancy-scroll direction="horizontal" hide>
									<div class="flex w-max items-start gap-3">
										${when(
											wholeEvent.length > 0,
											// Leading, because a type that belongs to every day and to
											// no day is what is always on sale — read before today.
											() => html`
												<schmancy-surface
													type="containerLowest"
													opacity="glass"
													elevated
													class=${COLUMN}
													data-reorder-zone="whole-event"
												>
													<div class="rounded-none bg-surface-containerLow">
														<div class="flex items-center gap-2.5 px-3 pt-2.5 pb-3">
															<span
																class="grid size-9 shrink-0 place-items-center rounded-full bg-surface-container"
																aria-hidden="true"
															>
																<schmancy-icon class="text-primary-default">event</schmancy-icon>
															</span>
															<schmancy-typography
																type="label"
																token="md"
																transform="uppercase"
																class="min-w-0 flex-1 truncate tracking-widest"
															>
																${this.t("storefront.typeSelector.wholeEventTitle")}
															</schmancy-typography>
															<schmancy-typography type="label" token="sm" class="shrink-0 tabular-nums text-surface-onVariant">
																${formatNumber(wholeEvent.length, this.locale)}
															</schmancy-typography>
														</div>
													</div>
													<schmancy-list surface="transparent" class=${LIST_AIR}>
														${repeat(
															wholeEvent,
															(r) => r.id,
															(r) => this.row(r),
														)}
													</schmancy-list>
												</schmancy-surface>
											`,
										)}
										${repeat(
											groups,
											(g) => g.day,
											(g) => html`
												<schmancy-surface
													type="containerLowest"
													opacity="glass"
													elevated
													class=${COLUMN}
													data-reorder-zone=${g.day}
													data-today=${g.day === today ? g.day : nothing}
													aria-current=${ifDefined(g.day === today ? "date" : undefined)}
												>
													${this.dayHeading(g, today, cloneable)}
													<schmancy-list surface="transparent" class=${LIST_AIR}>
														${repeat(
															g.rows,
															(r) => r.id,
															(r) => this.row(r),
														)}
													</schmancy-list>
												</schmancy-surface>
											`,
										)}
									</div>
								</schmancy-scroll>
							</schmancy-scroll-rail>
						`
				}
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		"programme-calendar": ProgrammeCalendarView;
	}
}
